import React, { useEffect, useState } from 'react';
import { Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ProgressData {
  status: 'processing' | 'completed' | 'error';
  currentDay: number;
  totalDays: number;
  currentMeal: string;
  message: string;
  percentage: number;
  timestamp: string;
}

interface MealPlanProgressModalProps {
  isOpen: boolean;
  onComplete: () => void;
  onError: (error: string) => void;
  onDismiss?: () => void;
}

export const MealPlanProgressModal: React.FC<MealPlanProgressModalProps> = ({
  isOpen,
  onComplete,
  onError,
  onDismiss
}) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState<ProgressData>({
    status: 'processing',
    currentDay: 0,
    totalDays: 7,
    currentMeal: '',
    message: 'Pregătesc generarea...',
    percentage: 0,
    timestamp: new Date().toISOString()
  });

  useEffect(() => {
    if (!isOpen) return;

    let retryCount = 0;
    const maxRetries = 3;
    let currentInterval = 3000; // Start cu 3 secunde

    // Polling în loc de SSE (mai simplu și mai compatibil)
    const pollProgress = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/nutrition/meal-plan/progress?token=${token}`
        );
        
        if (response.status === 429) {
          // Rate limit hit - increase interval exponentially
          retryCount++;
          if (retryCount <= maxRetries) {
            currentInterval = Math.min(currentInterval * 2, 10000); // Max 10s
            console.log(`Rate limit hit, backing off to ${currentInterval}ms`);
            return false; // Continue polling with new interval
          } else {
            onError('Prea multe cereri. Te rog reîncearcă mai târziu.');
            return true; // Stop polling
          }
        }
        
        if (response.ok) {
          const data: ProgressData = await response.json();
          setProgress(data);
          
          // Reset retry count on success
          retryCount = 0;
          currentInterval = 3000; // Reset la 3 secunde

          if (data.status === 'completed') {
            setTimeout(() => onComplete(), 1000);
            return true; // Stop polling
          } else if (data.status === 'error') {
            onError(data.message);
            return true; // Stop polling
          }
        } else if (response.status === 401) {
          retryCount++;
          if (retryCount > maxRetries) {
            onError('Sesiunea a expirat. Te rog reautentifică-te și încearcă din nou.');
            return true;
          }
          return false;
        } else if (response.status === 402) {
          const data = await response.json().catch(() => ({}));
          const available = data.available ?? 0;
          const required = data.required ?? 0;
          onError(`Nu ai suficiente credite! Ai ${available} credite disponibile, dar ai nevoie de ${required}. Achiziționează mai multe credite.`);
          return true;
        }
      } catch (error) {
        console.error('Error fetching progress:', error);
      }
      return false; // Continue polling
    };

    // Poll every 3 seconds (not every second!)
    let intervalId: ReturnType<typeof setTimeout> | undefined;
    
    const startPolling = async () => {
      const shouldStop = await pollProgress();
      if (!shouldStop) {
        intervalId = setTimeout(startPolling, currentInterval);
      }
    };

    // Initial poll after short delay
    setTimeout(startPolling, 500);

    return () => {
      if (intervalId) {
        clearTimeout(intervalId);
      }
    };
  }, [isOpen, onComplete, onError]);

  if (!isOpen) return null;

  // Calculare timp estimat
  const avgTimePerDay = 90; // 90 secunde per zi
  const remainingDays = progress.totalDays - progress.currentDay;
  const estimatedSeconds = remainingDays * avgTimePerDay;
  const estimatedMinutes = Math.ceil(estimatedSeconds / 60);

  // Fix: Adaugă +1 la currentDay pentru a face zilele 1-indexed
  const displayDay = progress.currentDay + 1;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-4 md:p-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="text-center mb-4 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1 md:mb-2">
            Generez planul tău de mese
          </h2>
          <p className="text-sm md:text-base text-gray-600">
            Procesul durează câteva minute
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-4 md:mb-8">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progres</span>
            <span className="font-semibold">{progress.percentage}%</span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500 ease-out"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        </div>

        {/* Current Status */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-blue-900">
                {progress.message}
              </p>
              {progress.currentMeal && (
                <p className="text-sm text-blue-700 mt-1">
                  {progress.currentMeal}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Days List - Optimizat pentru mobil */}
        <div className="mb-4 md:mb-6">
          {/* Desktop: Layout vertical clasic */}
          <div className="hidden md:block space-y-2 max-h-[50vh] overflow-y-auto">
            {Array.from({ length: progress.totalDays }, (_, i) => i + 1).map((day) => {
              const isCompleted = day < displayDay;
              const isCurrent = day === displayDay;

              return (
                <div
                  key={day}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    isCompleted
                      ? 'bg-green-50 border-green-200'
                      : isCurrent
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                        isCompleted
                          ? 'bg-green-500 text-white'
                          : isCurrent
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-300 text-gray-600'
                      }`}
                    >
                      {day}
                    </div>
                    <span
                      className={`font-medium ${
                        isCompleted
                          ? 'text-green-900'
                          : isCurrent
                          ? 'text-blue-900'
                          : 'text-gray-600'
                      }`}
                    >
                      Ziua {day}
                    </span>
                  </div>
                  <span
                    className={`text-sm ${
                      isCompleted
                        ? 'text-green-700'
                        : isCurrent
                        ? 'text-blue-700'
                        : 'text-gray-500'
                    }`}
                  >
                    {isCompleted ? '✓ Completă' : isCurrent ? '⟳ Procesare' : 'Așteptare'}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Mobile: Layout compact */}
          <div className="md:hidden">
            {progress.totalDays <= 7 ? (
              // Plan 7 zile: Grid compact 4+3 coloane
              <div className="space-y-2">
                {/* Prima linie: 4 zile */}
                <div className="grid grid-cols-4 gap-2">
                  {Array.from({ length: Math.min(4, progress.totalDays) }, (_, i) => i + 1).map((day) => {
                    const isCompleted = day < displayDay;
                    const isCurrent = day === displayDay;

                    return (
                      <div
                        key={day}
                        className={`flex flex-col items-center p-2 rounded-lg border transition-colors ${
                          isCompleted
                            ? 'bg-green-50 border-green-200'
                            : isCurrent
                            ? 'bg-blue-50 border-blue-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-semibold ${
                            isCompleted
                              ? 'bg-green-500 text-white'
                              : isCurrent
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-300 text-gray-600'
                          }`}
                        >
                          {day}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* A doua linie: restul zilelor (max 3) */}
                {progress.totalDays > 4 && (
                  <div className="grid grid-cols-3 gap-2">
                    {Array.from({ length: progress.totalDays - 4 }, (_, i) => i + 5).map((day) => {
                      const isCompleted = day < displayDay;
                      const isCurrent = day === displayDay;

                      return (
                        <div
                          key={day}
                          className={`flex flex-col items-center p-2 rounded-lg border transition-colors ${
                            isCompleted
                              ? 'bg-green-50 border-green-200'
                              : isCurrent
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-semibold ${
                              isCompleted
                                ? 'bg-green-500 text-white'
                                : isCurrent
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-300 text-gray-600'
                            }`}
                          >
                            {day}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <p className="text-center text-xs text-gray-600 mt-3">
                  Ziua {displayDay} din {progress.totalDays}
                </p>
              </div>
            ) : (
              // Plan 30 zile: Indicator compact
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-3">Progres zilnic</p>
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <div className="w-16 h-16 bg-blue-500 text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                      {displayDay}
                    </div>
                    <div className="text-3xl text-gray-400">/</div>
                    <div className="w-16 h-16 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center text-2xl font-bold">
                      {progress.totalDays}
                    </div>
                  </div>
                  <p className="text-sm text-blue-900 font-medium">
                    Ziua {displayDay} din {progress.totalDays}
                  </p>
                  <div className="mt-3 flex justify-center gap-1">
                    {Array.from({ length: Math.min(progress.totalDays, 30) }, (_, i) => i + 1).map((day) => (
                      <div
                        key={day}
                        className={`w-1.5 h-1.5 rounded-full ${
                          day < displayDay
                            ? 'bg-green-500'
                            : day === displayDay
                            ? 'bg-blue-500'
                            : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Time Estimate */}
        <div className="text-center text-xs md:text-sm text-gray-600 mb-4 md:mb-6">
          <p>
            Timp estimat rămas:{' '}
            <span className="font-semibold text-gray-900">
              {estimatedMinutes > 0 ? `~${estimatedMinutes} minute` : 'Mai puțin de 1 minut'}
            </span>
          </p>
        </div>

        {/* Dismiss Button */}
        <div className="border-t pt-4 md:pt-6">
          <button
            onClick={() => {
              if (onDismiss) {
                onDismiss();
              } else {
                navigate('/dashboard');
              }
            }}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            <Home className="w-5 h-5" />
            Înapoi la Dashboard
          </button>
          <p className="text-xs text-center text-gray-500 mt-3">
            💡 Procesul continuă în background. Poți reveni mai târziu să vezi planul complet.
          </p>
        </div>
      </div>
    </div>
  );
};
